import {
  clamp,
  compose,
  invert,
  multiply,
  transformPoint,
  transformRect,
  type Mat2D,
  type Rect2D,
  type Vec2,
} from "./math2d.js";

export interface Camera2DOptions {
  x?: number;
  y?: number;
  /** Above 1 magnifies. */
  zoom?: number;
  /** Radians. The world turns the other way, so a value here tilts the view. */
  rotation?: number;
  minZoom?: number;
  maxZoom?: number;
}

/**
 * A 2D camera: where the view is looking, and how far in.
 *
 * The camera's position is the world point at the **center** of the viewport,
 * not its top-left corner. That is the difference between a camera that can be
 * pointed at a thing — `camera.lookAt(player)` — and one where every caller
 * has to subtract half the screen first and gets it wrong when the window
 * resizes.
 */
export class Camera2D {
  #x: number;
  #y: number;
  #zoom: number;
  #rotation: number;
  /** Viewport size in pixels, set by the renderer each frame. */
  #width = 1;
  #height = 1;

  minZoom: number;
  maxZoom: number;

  constructor(options: Camera2DOptions = {}) {
    this.#x = options.x ?? 0;
    this.#y = options.y ?? 0;
    this.minZoom = options.minZoom ?? 0.05;
    this.maxZoom = options.maxZoom ?? 64;
    this.#zoom = clamp(options.zoom ?? 1, this.minZoom, this.maxZoom);
    this.#rotation = options.rotation ?? 0;
  }

  get x(): number {
    return this.#x;
  }
  set x(v: number) {
    this.#x = v;
  }

  get y(): number {
    return this.#y;
  }
  set y(v: number) {
    this.#y = v;
  }

  get position(): Vec2 {
    return { x: this.#x, y: this.#y };
  }

  get zoom(): number {
    return this.#zoom;
  }
  set zoom(v: number) {
    this.#zoom = clamp(v, this.minZoom, this.maxZoom);
  }

  get rotation(): number {
    return this.#rotation;
  }
  set rotation(v: number) {
    this.#rotation = v;
  }

  get viewportWidth(): number {
    return this.#width;
  }

  get viewportHeight(): number {
    return this.#height;
  }

  /** Called by the renderer before it draws; sizes the projection. */
  setViewport(width: number, height: number): void {
    this.#width = Math.max(1, width);
    this.#height = Math.max(1, height);
  }

  /** Point the camera at a world position. */
  lookAt(target: Vec2): void {
    this.#x = target.x;
    this.#y = target.y;
  }

  /**
   * Ease towards a target, framerate-independently.
   *
   * `smoothing` is the fraction of the remaining distance left after one
   * second — 0.01 means "close 99% of the gap per second". Expressed that way
   * rather than as a per-frame lerp factor because a per-frame factor makes
   * the camera follow faster on a faster machine, which is the classic bug in
   * a hand-rolled follow camera.
   */
  follow(target: Vec2, dt: number, smoothing = 0.01): void {
    const t = 1 - Math.pow(clamp(smoothing, 0, 1), dt);
    this.#x += (target.x - this.#x) * t;
    this.#y += (target.y - this.#y) * t;
  }

  /** World -> view pixels, with the camera's center at the viewport's center. */
  get viewMatrix(): Mat2D {
    // The camera's own placement in the world, which is then undone to get
    // world -> view. Its scale is the *reciprocal* of the zoom: at zoom 2 the
    // camera covers half as much world, so one world unit becomes two pixels
    // once inverted. Composing with the zoom directly inverts the meaning and
    // makes zooming in show more.
    const camera = compose(
      { x: this.#x, y: this.#y },
      this.#rotation,
      { x: 1 / this.#zoom, y: 1 / this.#zoom },
    );
    const toCenter: Mat2D = {
      a: 1,
      b: 0,
      c: 0,
      d: 1,
      tx: this.#width / 2,
      ty: this.#height / 2,
    };
    return multiply(invert(camera), toCenter);
  }

  worldToScreen(world: Vec2): Vec2 {
    return transformPoint(this.viewMatrix, world);
  }

  screenToWorld(screen: Vec2): Vec2 {
    return transformPoint(invert(this.viewMatrix), screen);
  }

  /**
   * The world rectangle currently on screen.
   *
   * Axis-aligned, so under rotation it is the bounding box of the rotated view
   * and therefore larger than what is really visible. That is the right way
   * round for culling: too big keeps a sprite that was not needed, too small
   * pops one out that was.
   */
  get visibleBounds(): Rect2D {
    const screen: Rect2D = { x: 0, y: 0, width: this.#width, height: this.#height };
    return transformRect(invert(this.viewMatrix), screen);
  }

  /** Zoom about a screen point, keeping the world under it still. */
  zoomAt(factor: number, screen: Vec2): void {
    const before = this.screenToWorld(screen);
    this.zoom = this.#zoom * factor;
    const after = this.screenToWorld(screen);
    this.#x += before.x - after.x;
    this.#y += before.y - after.y;
  }
}
